import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export const TimeSelect = ({ value, onChange, disabled, className = '' }) => {
  // Parse 24hr value (e.g. "14:30") into hour/minute/period
  const parsed = (() => {
    if (!value) return { hour: '', minute: '', period: '' };
    const [h, m] = value.split(':').map(Number);
    if (isNaN(h)) return { hour: '', minute: '', period: '' };
    const period = h < 12 ? 'AM' : 'PM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return { hour: String(hour12), minute: String(m), period };
  })();

  const update = (field, val) => {
    const h = field === 'hour' ? val : parsed.hour;
    const m = field === 'minute' ? val : parsed.minute;
    const p = field === 'period' ? val : parsed.period;
    if (!h || m === '' || !p) {
      // Partial — set defaults for missing
      const hr = h || '9';
      const mn = m === '' ? '0' : m;
      const pr = p || 'AM';
      let h24 = Number(hr);
      if (pr === 'PM' && h24 !== 12) h24 += 12;
      if (pr === 'AM' && h24 === 12) h24 = 0;
      onChange(`${String(h24).padStart(2, '0')}:${String(mn).padStart(2, '0')}`);
      return;
    }
    let h24 = Number(h);
    if (p === 'PM' && h24 !== 12) h24 += 12;
    if (p === 'AM' && h24 === 12) h24 = 0;
    onChange(`${String(h24).padStart(2, '0')}:${String(Number(m)).padStart(2, '0')}`);
  };

  const hasValue = value && value !== '';

  return (
    <div className={`flex gap-1.5 ${className}`}>
      <Select value={parsed.hour || undefined} onValueChange={v => update('hour', v)} disabled={disabled}>
        <SelectTrigger className={`w-[70px] ${!hasValue ? 'text-muted-foreground' : ''}`}>
          <SelectValue placeholder="Hr" />
        </SelectTrigger>
        <SelectContent>
          {HOURS.map(h => <SelectItem key={h} value={String(h)}>{h}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={parsed.minute !== '' ? parsed.minute : undefined} onValueChange={v => update('minute', v)} disabled={disabled}>
        <SelectTrigger className={`w-[70px] ${!hasValue ? 'text-muted-foreground' : ''}`}>
          <SelectValue placeholder="Min" />
        </SelectTrigger>
        <SelectContent className="max-h-48">
          {MINUTES.map(m => <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={parsed.period || undefined} onValueChange={v => update('period', v)} disabled={disabled}>
        <SelectTrigger className={`w-[72px] ${!hasValue ? 'text-muted-foreground' : ''}`}>
          <SelectValue placeholder="AM/PM" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

export const formatTime12hr = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  if (isNaN(h)) return time24;
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

export default TimeSelect;
